import { writable } from 'svelte/store'

export const showSessionTimeoutModal = writable(false)

let inactivityTimeout
const INACTIVITY_LIMIT = 20 * 60 * 1000 // 20 minutes
const WARNING_BEFORE = 2 * 60 * 1000 // show modal 2 minutes before

let resetTimerHandler

const CHANNEL_NAME = 'auth_channel'
const STORAGE_KEY = 'session_timeout_event'
const isBrowser = typeof window !== 'undefined'
const hasBroadcastChannel = isBrowser && typeof BroadcastChannel !== 'undefined'
const VALID_MESSAGE_TYPES = ['SHOW_WARNING', 'CONFIRM_LOGOUT', 'CONTINUE_SESSION', 'RESET_TIMER']

let lastActivityBroadcast = 0
const ACTIVITY_BROADCAST_THROTTLE = 5000

class SessionTimeout {
  constructor() {
    this.channel = null
    this.isInitialized = false
    this.useStorageFallback = false

    if (isBrowser) {
      this.init()
    }
  }

  init() {
    if (this.isInitialized) return

    try {
      if (hasBroadcastChannel) {
        this.channel = new BroadcastChannel(CHANNEL_NAME)

        this.channel.onmessage = event => {
          if (!event?.data || typeof event.data !== 'object') {
            return
          }

          const { type } = event.data

          if (typeof type !== 'string' || !VALID_MESSAGE_TYPES.includes(type)) {
            return
          }

          window.dispatchEvent(new CustomEvent(`session-${type.toLowerCase()}`))
        }
      } else {
        this.useStorageFallback = true
        this.setupStorageFallback()
      }

      this.isInitialized = true
    } catch (error) {
      console.warn('BroadcastChannel initialization failed, using storage fallback:', error)
      this.useStorageFallback = true
      this.setupStorageFallback()
      this.isInitialized = true
    }
  }

  setupStorageFallback() {
    window.addEventListener('storage', event => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const data = JSON.parse(event.newValue)

          if (!data || typeof data !== 'object') {
            return
          }

          const { type, timestamp } = data

          if (typeof type !== 'string' || !VALID_MESSAGE_TYPES.includes(type)) {
            return
          }

          if (typeof timestamp !== 'number') {
            return
          }

          if (Date.now() - timestamp < 1000) {
            window.dispatchEvent(new CustomEvent(`session-${type.toLowerCase()}`))
          }
        } catch (error) {
          console.error('Failed to parse storage event:', error)
        }
      }
    })
  }

  broadcast(type) {
    if (!this.isInitialized || !VALID_MESSAGE_TYPES.includes(type)) {
      return
    }

    try {
      if (this.channel && !this.useStorageFallback) {
        this.channel.postMessage({ type })
      } else if (this.useStorageFallback) {
        const event = JSON.stringify({ type, timestamp: Date.now() })
        localStorage.setItem(STORAGE_KEY, event)
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (error) {
      console.error('Failed to broadcast:', error)
    }
  }

  broadcastShowWarning() {
    this.broadcast('SHOW_WARNING')
  }

  broadcastConfirmLogout() {
    this.broadcast('CONFIRM_LOGOUT')
  }

  broadcastContinueSession() {
    this.broadcast('CONTINUE_SESSION')
  }

  broadcastResetTimer() {
    const now = Date.now()
    if (now - lastActivityBroadcast >= ACTIVITY_BROADCAST_THROTTLE) {
      this.broadcast('RESET_TIMER')
      lastActivityBroadcast = now
    }
  }

  destroy() {
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
    this.isInitialized = false
  }
}

export const sessionTimeout = new SessionTimeout()

export function initializeSessionTimeoutModal() {
  clearTimeout(inactivityTimeout)

  resetTimerHandler = () => {
    clearTimeout(inactivityTimeout)
    inactivityTimeout = setTimeout(() => {
      showSessionTimeoutModal.set(true)
    }, INACTIVITY_LIMIT - WARNING_BEFORE)
    sessionTimeout.broadcastResetTimer()
  }

  window.addEventListener('session-reset_timer', resetTimer)

  const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keydown', 'touchstart']
  events.forEach(e => window.addEventListener(e, resetTimerHandler))

  resetTimerHandler()
}

export function cleanupInactivityListeners() {
  clearTimeout(inactivityTimeout)
  const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keydown', 'touchstart']
  events.forEach(e => window.removeEventListener(e, resetTimerHandler))
  window.removeEventListener('session-reset_timer', resetTimer)
}

function resetTimer() {
  clearTimeout(inactivityTimeout)
  inactivityTimeout = setTimeout(() => {
    showSessionTimeoutModal.set(true)
  }, INACTIVITY_LIMIT - WARNING_BEFORE)
}
